import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, User } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { PageLoader } from '@shared/components/PageLoader';
import { connectsAPI } from '@/utils/supabase/client';
import type { ConnectsContact } from '@/types/connects';
import { formatDistanceToNow } from 'date-fns';

export function ContactsList() {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState<ConnectsContact[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = (q?: string) => {
    setLoading(true);
    connectsAPI.getContacts({ q, limit: 100 })
      .then(({ contacts: c, total: t }) => { setContacts(c); setTotal(t); })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const timer = setTimeout(() => load(search.length >= 2 ? search : undefined), 300);
    return () => clearTimeout(timer);
  }, [search]);

  return (
    <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6 overflow-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2 font-serif">
            <User className="h-6 w-6" />
            Contacts
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{total} people tracked</p>
        </div>
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search contacts..."
            className="pl-9 w-full"
          />
        </div>
      </div>

      {loading ? <PageLoader /> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {contacts.map((ct) => (
            <Card
              key={ct.id}
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate(`/connects/contacts/${ct.id}`)}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="h-9 w-9 rounded-full bg-muted flex items-center justify-center shrink-0">
                    <User className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{ct.display_name}</p>
                    {ct.title && <p className="text-xs text-muted-foreground truncate">{ct.title}</p>}
                    {(ct as any).companies?.name && (
                      <p className="text-xs text-muted-foreground truncate">{(ct as any).companies.name}</p>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="secondary" className="text-xs">{ct.mention_count} mentions</Badge>
                      <span className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(ct.last_mentioned_at), { addSuffix: true })}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          {!loading && contacts.length === 0 && (
            <div className="col-span-full text-center py-12 text-sm text-muted-foreground">
              No contacts found{search ? ` for "${search}"` : ' yet — start posting to #knox'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
